import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ErrorBoundary } from '../../../components/error-boundary';
import Footer from '../../../components/Footer';
import Loading from '../../../components/loading/loading';
import { HttpClientError } from '../../../controllers/http-client/http-client';
import { usePage } from '../../../queries/ats/layout/queries';
import { Layout } from '../../../queries/ats/layout/types';
import { pageIsContaingCarousel } from '../../../utils/atsLayoutUtils';
import StickyHeader from '../../app/StickyHeader';
import { NotFound } from '../../not-found/not-found';
import { PageHeading } from '../page-heading/page-heading';
import { Section } from '../section/section';
import { SectionContextProvider } from '../section/section-context';
import { EmptyPage } from './empty-page';
import { PageContextProvider } from './page-context';
import css from './page.module.scss';

interface PageProps {
  slug: string;
}

export function Page({ slug }: PageProps) {
  const { data: page, error, isLoading } = usePage(slug);

  if (error instanceof HttpClientError && error.status === 404) {
    return <NotFound />;
  }

  if (isLoading) {
    return <Loading size={64} contained />;
  }

  if (!page) {
    return <NotFound />;
  }

  if (page.sections.length === 0) {
    return <EmptyPage label={page.label} />;
  }

  return (
    <PageContextProvider page={page}>
      <Helmet>
        <title>{page.label} - Altibox TV</title>
      </Helmet>

      <StickyHeader />

      <main className={css.container}>
        {displayPageHeading(page) && <PageHeading>{page.label}</PageHeading>}

        {page.sections.map((section) => (
          <ErrorBoundary key={section.link}>
            <SectionContextProvider section={section}>
              <Section key={section.link} page={page} section={section} />
            </SectionContextProvider>
          </ErrorBoundary>
        ))}
      </main>

      <Footer />
    </PageContextProvider>
  );
}

function displayPageHeading(page: Layout.PageView) {
  return page.label.length > 0 && !pageIsContaingCarousel(page);
}
